import { PageHero, useTitle } from "../components/PageHero";
import { Reveal, Tag } from "../components/Reveal";
import { PrimaryBtn } from "../components/ui";
import { CTABand } from "../components/CTABand";
import { IMG, SITE } from "../data/site";

const SECTIONS = [
  {
    title: "Who We Are",
    text: "SABAKT is a Saudi Arabia-based industrial engineering and fabrication company serving plants, contractors and industrial operators across the Kingdom. We exist for one reason: to keep industrial equipment working longer, failing less, and returning to service faster.",
  },
  {
    title: "What We Do",
    text: "Industrial fabrication, wear protection and wear parts, equipment reconditioning, industrial maintenance support, storage tanks, and specialized industrial products — engineered for cement, steel, mining, concrete, oil & gas, power, manufacturing and infrastructure operations.",
  },
  {
    title: "Engineering Approach",
    text: "Solutions begin with understanding the equipment, the operating conditions and the failure mechanism. A worn chute is not a plate order — it is an abrasion pattern, an impact load and a flow geometry. We engineer against the cause.",
  },
  {
    title: "Manufacturing & Fabrication",
    text: "Heavy-duty structural and process steelwork fabricated to drawings, dimensions and site conditions: platforms, walkways, chutes, hoppers, bins, ducts, supports, frames and custom components.",
  },
  {
    title: "Wear Protection Expertise",
    text: "Hardox wear plates, Chromium Carbide Overlay (CCO), fabricated wear liners and hardfacing — specified against the actual wear mechanism so protection lasts to the planned stop, not past it.",
  },
  {
    title: "Reconditioning Capability",
    text: "Excavator bucket reconditioning, crack repair, dimensional restoration, hardfacing and component reclamation — returning heavy components to service where replacement isn't the best answer.",
  },
  {
    title: "The Saudi Market",
    text: "Supporting Saudi industrial growth through local fabrication, equipment life extension, maintenance support and industrial supply. Based in Riyadh, executing Kingdom-wide — aligned with the localization goals of Vision 2030 through real capability, not slogans.",
  },
  {
    title: "Quality & Safety",
    text: "Work is executed to applicable project specifications and required standards. [VERIFY WITH SABAKT — certifications, welding qualifications, quality systems and HSE documentation to be listed here once confirmed. We do not display credentials we cannot substantiate.]",
  },
];

export default function About() {
  useTitle("About SABAKT — Industrial Engineering & Fabrication | Riyadh, Saudi Arabia");
  return (
    <main>
      <PageHero image={IMG.hero} eyebrow="About SABAKT" testid="about-hero"
        title="Capability, Not Corporate Language." accent="Capability,"
        text="SABAKT is an industrial engineering and fabrication partner helping Saudi plants reduce downtime, extend equipment life and keep critical operations running." />

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-5xl px-4 sm:px-8 lg:px-16">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title}>
              <div className="grid gap-4 border-t border-seam py-10 md:grid-cols-[100px_1fr_1.6fr] md:gap-10" data-testid={`about-section-${i}`}>
                <span className="font-display text-5xl font-black text-seam">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-frost">{s.title}</h2>
                <p className="text-base leading-relaxed text-cadmium">{s.text}</p>
              </div>
            </Reveal>
          ))}

          <Reveal>
            <div className="mt-6 border border-seam bg-iron p-8 lg:p-10" data-testid="about-partners">
              <Tag>Our Partners</Tag>
              <h2 className="mt-3 font-display text-2xl font-extrabold uppercase text-frost">Diffusion Engineers Ltd.</h2>
              <p className="mt-4 text-base leading-relaxed text-cadmium">
                SABAKT is the authorized channel partner of Diffusion Engineers Limited — one of India's most prestigious engineering companies specializing in wear solutions and maintenance technology. Through this partnership, Saudi customers get local access to specialized welding consumables, hardfacing solutions, wear-resistant materials and application-level engineering support for reconditioning and industrial wear applications.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-12 flex flex-wrap gap-4">
            <PrimaryBtn to="/contact" testid="about-contact-btn">Talk to an Engineer</PrimaryBtn>
          </Reveal>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-widest text-cadmium/50">
            {SITE.city} · {SITE.email} · {SITE.phone}
          </p>
        </div>
      </section>
      <CTABand />
    </main>
  );
}
